<template>
  <page-container
    class="hotelux-home-page"
    :show-navbar="false"
    show-header
    :logo-url="logoUrl"
    :show-currency="true"
  >
    <main class="hotelux-home">
      <section class="hero">
        <el-carousel
          class="hero-carousel"
          :autoplay="heroBanners.length > 1"
          :interval="4000"
          :arrow="heroBanners.length > 1 ? 'hover' : 'never'"
          indicator-position="none"
        >
          <el-carousel-item
            v-for="banner in heroBanners"
            :key="banner._id || banner.img"
          >
            <button
              type="button"
              class="hero-banner"
              :class="{ clickable: isClickable(banner) }"
              :style="{ backgroundImage: `url(${heroImage(banner)})` }"
              @click="handleEntryClick(banner)"
            >
              <img
                v-if="banner.extendedProp?.partnerLogo"
                class="hero-partner-logo"
                :src="banner.extendedProp.partnerLogo"
                alt=""
              />
              <span v-if="heroTitle(banner)" class="hero-title">
                {{ heroTitle(banner) }}
              </span>
              <span v-if="heroSubtitle(banner)" class="hero-subtitle">
                {{ heroSubtitle(banner) }}
              </span>
              <span v-if="heroAction(banner)" class="hero-action">
                {{ heroAction(banner) }}
              </span>
            </button>
          </el-carousel-item>
        </el-carousel>
        <search-bar
          class="hero-search"
          @search-click="handleSearch"
          @search-change="handleSearchChange"
        />
      </section>

      <section v-if="homeStore.exclusiveOffers.length" class="home-section">
        <h2>{{ getI18nText(homeStore.sectionTitles.exclusiveOffers) }}</h2>
        <div class="offer-list">
          <button
            v-for="offer in homeStore.exclusiveOffers"
            :key="offer._id"
            type="button"
            class="offer-card"
            @click="handleEntryClick(offer)"
          >
            <img :src="desktopImage(offer)" alt="" />
            <span v-if="getI18nText(offer.badgeI18n)" class="offer-badge">
              {{ getI18nText(offer.badgeI18n) }}
            </span>
            <span class="offer-content">
              <strong>{{ getI18nText(offer.titleI18n) }}</strong>
              <span v-if="getI18nText(offer.extendedProp?.timeI18n)">
                {{ getI18nText(offer.extendedProp?.timeI18n) }}
              </span>
              <span v-if="offer.extendedProp?.price" class="offer-price">
                {{ offer.extendedProp.price }}
              </span>
            </span>
          </button>
        </div>
      </section>

      <section class="home-section curated-section">
        <h2>{{ getI18nText(homeStore.sectionTitles.curatedCollections) }}</h2>
        <div v-loading="hotelsLoading" class="hotel-list">
          <hotel-card
            v-for="hotel in hotels"
            :key="hotel._id"
            :hotel="hotel"
            @select="handleSelectHotel(hotel)"
          />
        </div>
      </section>

      <section v-if="homeStore.destinations.length" class="destination-links">
        <div
          v-for="destination in homeStore.destinations"
          :key="getI18nText(destination.nameI18n)"
          class="destination-group"
        >
          <h3>{{ getI18nText(destination.nameI18n) }}</h3>
          <button
            v-for="hotel in destination.hotels"
            :key="hotel._id"
            type="button"
            @click="navigateToHotel(hotel)"
          >
            {{ getI18nText(hotel.nameI18n) }}
          </button>
        </div>
      </section>
    </main>
  </page-container>
</template>

<script setup>
import HotelCard from "@packages/booking/hotel/HotelCard/V2.vue";
import { storeToRefs } from "pinia";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import SearchBar from "@/distributions/consumer/y/components/SearchBar/index.vue";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useHoteluxHomeStoreHook } from "@/distributions/consumer/y/store/hoteluxHome";
import { recordLocationHistory } from "@/distributions/consumer/y/utils/historyHelper";
import { syncHotelDetailLocation } from "@/distributions/consumer/y/utils/hotelNavigation";
import { useHotels } from "@/hooks/useHotels";
import { getI18nText } from "@/plugins/i18n";
import { useUserStoreHook } from "@/store/modules/user";
import { paramsToQuery } from "@/utils/tool";

import { useYChannelStoreHook } from "../../../../store/channel";

const router = useRouter();
const { t } = useI18n();
const channelStore = useYChannelStoreHook();
const hotelStore = useHotelStoreHook();
const homeStore = useHoteluxHomeStoreHook();
const userStore = useUserStoreHook();
const { searchModel } = storeToRefs(hotelStore);
const { language, preferred } = storeToRefs(userStore);
const DEFAULT_HERO = {
  img: "https://uploads.ytripos.com/uploaded/lkTce8_RRZXTg2mvkzB-I1uPdazV/y-web-index-bg2.png"
};

const logoUrl = computed(
  () => channelStore.configs?.pages?.Index?.logoUrl || ""
);
const configuredBanners = computed(() => {
  const list = channelStore.configs?.pages?.Index?.banners?.list || [];
  return list.filter(item => item.show?.({ user: userStore.user }) ?? true);
});
const heroBanners = computed(() => {
  const entryBanners = homeStore.banners.filter(
    item =>
      item.imageUrl ||
      getI18nText(item.imageUrlI18n) ||
      item.imageAlternative?.imageUrl
  );
  const banners = entryBanners.length ? entryBanners : configuredBanners.value;
  return banners.length ? banners : [DEFAULT_HERO];
});
const curatedFilter = computed(() => ({
  hotels: homeStore.curatedHotelIds.map(_id => ({ _id }))
}));

const {
  hotels,
  loading: hotelsLoading,
  fetchHotels
} = useHotels({
  pageSize: 6,
  stay: searchModel.value.stay,
  filter: curatedFilter,
  preferred
});

const desktopImage = item =>
  item?.imageAlternative?.imageUrl ||
  getI18nText(item?.imageUrlI18n) ||
  item?.imageUrl ||
  "";
const heroImage = item =>
  item?.imageAlternative?.imageUrl ||
  getI18nText(item?.imageUrlI18n) ||
  item?.imageUrl ||
  item?.img ||
  "";
const heroTitle = item =>
  item?._id ? getI18nText(item.titleI18n) : item?.title ? t(item.title) : "";
const heroSubtitle = item =>
  item?._id
    ? getI18nText(item.subtitleI18n)
    : item?.subtitle
    ? t(item.subtitle)
    : item?.content
    ? t(item.content)
    : "";
const heroAction = item =>
  item?._id
    ? getI18nText(item.extendedProp?.actionLabelI18n)
    : item?.btn
    ? t(item.btn)
    : "";
const entryLink = item =>
  item?.linkAlternative?.path ? item.linkAlternative : item?.link;
const isClickable = item =>
  Boolean(item?.onClick || entryLink(item)?.path || item?.target?.ref);

const handleEntryClick = item => {
  if (item?.onClick) {
    item.onClick({ router, userStore });
    return;
  }

  const target = item?.target?.ref;
  const targetRef = target?._id || target;
  if (targetRef) {
    navigateToHotel(typeof target === "object" ? target : { _id: targetRef });
    return;
  }

  const link = entryLink(item);
  if (!link?.path) return;
  if (["webpage", "browser", "externalApp"].includes(link.type)) {
    const target = link.extendedProp?.target === "_self" ? "_self" : "_blank";
    if (target === "_self") {
      window.location.assign(link.path);
      return;
    }
    window.open(link.path, target, "noopener,noreferrer");
    return;
  }
  router.push(link.path);
};

const ROUTER_PATH_MAP = {
  moduleTpHotel: "/hotel/detail",
  city: "/hotel/list",
  googleMapPolitical: "/hotel/list",
  search: "/hotel/list"
};
const handleSearchChange = () => {
  const query = paramsToQuery();
  if (query?.locationType === "moduleTpHotel") handleSearch();
};
const handleSearch = () => {
  recordLocationHistory(hotelStore.hotelSearchModel.location);
  const query = paramsToQuery();
  router.push({
    path: ROUTER_PATH_MAP[query.locationType] || "/hotel/list",
    query
  });
};

const navigateToHotel = hotel => {
  hotelStore.SET_SELECTED_HOTEL(hotel);
  const locationQuery = syncHotelDetailLocation(
    hotelStore,
    hotel,
    language.value
  );
  router.push({
    path: "/hotel/detail",
    query: {
      ...locationQuery,
      checkIn: hotelStore.searchModel.stay.date.checkIn,
      checkOut: hotelStore.searchModel.stay.date.checkOut,
      numberOfAdults: hotelStore.searchModel.stay.guest.numberOfAdults,
      numberOfChildren: hotelStore.searchModel.stay.guest.numberOfChildren,
      numberOfRooms: hotelStore.searchModel.stay.guest.numberOfRooms,
      hotelId: hotel._id
    }
  });
};
const handleSelectHotel = hotel => navigateToHotel(hotel);

onMounted(async () => {
  await homeStore.fetchHome();
  fetchHotels();
});

defineOptions({ name: "HoteluxHome" });
</script>

<style lang="scss" scoped>
.hotelux-home-page {
  :deep(.wrapper) {
    min-width: 1200px;
    padding-top: 0 !important;
  }

  :deep(.header) {
    --y-currency-selector-color: #292929;

    position: relative;
    height: 114px;
    padding: 28px max(60px, calc((100% - 1520px) / 2));
    color: #292929;
    background: #fff;
  }

  :deep(.header .y-current-user) {
    --y-text-color: #292929;
  }
}

.hotelux-home {
  width: 100%;
  color: #292929;
  background: #fff;
}

.hero {
  position: relative;
  width: 100%;
  height: min(900px, 78vh);
  min-height: 680px;
  margin-bottom: 80px;
}

.hero-carousel,
:deep(.hero-carousel .el-carousel__container) {
  height: 100%;
}

.hero-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 180px 60px 170px;
  color: #fff;
  background-position: center;
  background-size: cover;
  border: 0;

  &.clickable {
    cursor: pointer;
  }
}

.hero-title {
  max-width: 1100px;
  font-family: var(--y-title-font);
  font-size: 56px;
  line-height: 1.15;
  text-align: center;
  text-shadow: 0 2px 12px rgb(0 0 0 / 28%);
}

.hero-partner-logo {
  max-width: 220px;
  max-height: 88px;
  margin-bottom: 30px;
  object-fit: contain;
}

.hero-subtitle {
  max-width: 960px;
  margin-top: 24px;
  font-size: 22px;
  line-height: 1.45;
  text-align: center;
  text-shadow: 0 2px 10px rgb(0 0 0 / 28%);
}

.hero-action {
  min-width: 160px;
  padding: 12px 24px;
  margin-top: 44px;
  font-size: 14px;
  text-align: center;
  background: rgb(0 0 0 / 28%);
  border: 1px solid rgb(255 255 255 / 80%);
}

.hero-search {
  --search-bar-inner-width: min(1160px, calc(100vw - 120px));

  position: absolute;
  right: 0;
  bottom: -50px;
  left: 0;
  z-index: 2;

  :deep(.search-bar-inner) {
    min-height: 100px;
    padding: 20px 34px;
    box-shadow: 0 12px 36px rgb(0 0 0 / 14%);
  }
}

.home-section {
  width: min(1500px, calc(100% - 120px));
  padding: 70px 0 40px;
  margin: 0 auto;

  h2 {
    margin-bottom: 44px;
    font-family: var(--y-title-font);
    font-size: 42px;
    font-weight: 500;
    line-height: 1;
    text-align: left;
  }
}

.offer-list {
  display: flex;
  gap: 24px;
  overflow-x: auto;
}

.offer-card {
  position: relative;
  flex: 0 0 480px;
  min-height: 330px;
  padding: 0;
  overflow: hidden;
  color: #fff;
  text-align: left;
  cursor: pointer;
  background: #282828;
  border: 0;

  &::after {
    position: absolute;
    inset: 35% 0 0;
    content: "";
    background: linear-gradient(180deg, transparent, rgb(0 0 0 / 78%));
  }

  img {
    width: 100%;
    height: 100%;
    min-height: 330px;
    object-fit: cover;
    transition: transform 0.35s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
}

.offer-badge {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 1;
  padding: 7px 12px;
  font-size: 12px;
  letter-spacing: 0.06em;
  background: rgb(0 0 0 / 68%);
}

.offer-content {
  position: absolute;
  right: 24px;
  bottom: 24px;
  left: 24px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 7px;

  strong {
    font-family: var(--y-title-font);
    font-size: 26px;
    font-weight: 500;
  }
}

.offer-price {
  font-size: 18px;
}

.curated-section {
  padding-bottom: 120px;
}

.hotel-list {
  display: flex;
  gap: 28px;
  min-height: 420px;
  overflow-x: auto;

  :deep(.hotel-card) {
    flex: 0 0 443px;
  }
}

.offer-list,
.hotel-list {
  padding-bottom: 24px;
  scrollbar-color: #292929 #e4e4e4;
  scrollbar-width: thin;
}

.destination-links {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 36px;
  width: 100%;
  padding: 70px max(60px, calc((100% - 1520px) / 2));
  color: rgb(255 255 255 / 72%);
  background: #282828;
}

.destination-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  h3 {
    margin-bottom: 24px;
    font-family: var(--y-title-font);
    font-size: 22px;
    font-weight: 500;
    color: #fff;
  }

  button {
    padding: 0;
    font-size: 15px;
    color: inherit;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;

    &:hover {
      color: #fff;
      text-decoration: underline;
    }
  }
}

@media screen and (width <= 1440px) {
  .home-section {
    width: 1180px;
  }
}
</style>
